import { IRFEListBoxItem } from '../../pages/mylibrary/typings/myLibraryViewModel';
import LookupApiService from '../api/lookupApiService';
import { IApplyTagsRequest, ILibraryTagsRequest, IUpdateTagsRequest, IUpdateTagsResult } from '../api/models/tags';
import TagsApiService from '../api/tagsApiService';
import TagItem from '../typings/tags/tagItem';
import { TagsSortOrder } from '../typings/tags/tagsSortOrder';
import AxiosCancellationToken from '../utils/axiosCancellationToken';
import { PascalCase } from '../utils/pascalCase';
import TagUtils from '../utils/tagUtils';
import { makeAutoObservable } from 'mobx';

export interface ITagsStore {
	get uniqueNewTags(): TagItem[];
	addNewTag(tagName: string): void;
	addTagsWithInfoToDocuments(documentIds: number[]): Promise<void>;
	addToPendingTags(): void;
	applyChanges(): Promise<IUpdateTagsResult>;
	deleteNewTag(tagName: string): void;
	deleteSelectedTag(tag: TagItem): void;
	existingTags: TagItem[];
	fetchTags(): Promise<void>;
	newTags: TagItem[];
	pendingTags: TagItem[];
	requestCancellation(): void;
	search(tagName: string): Promise<void>;
	searchQuery: string;
	selectedTags: TagItem[];
	selectTag(tag: TagItem): void;
	sortTags(direction: TagsSortOrder): Promise<void>;
	tagExists(tagName: string): boolean;
	sendAddTagsWithInfoRequest(documentIds: number[], tags: TagItem[]): Promise<void>;
	setNewTags(tags: TagItem[]): void;
	addExistingTag(tag: TagItem): void;
	resetExistingTags(): void;
}

export default class TagsStore implements ITagsStore {
	existingTags: TagItem[] = [];
	pendingTags: TagItem[] = [];
	newTags: TagItem[] = [];
	selectedTags: TagItem[] = [];

	searchQuery = '';

	private sortDirection: TagsSortOrder = TagsSortOrder.ByFrequency;
	private tagFound = true;
	private cancellationToken: AxiosCancellationToken;

	constructor(
		private readonly libraryId: number,
		private readonly lookupApiService: LookupApiService,
		private readonly tagsApiService: TagsApiService
	) {
		makeAutoObservable(this);
	}

	get uniqueNewTags() {
		return this.newTags.filter(tag => !this.tagExists(tag.originalName));
	}

	tagExists(tagName: string) {
		return !!this.existingTags.find(existingTag =>
			TagUtils.equals(existingTag.updatedName ?? existingTag.originalName, tagName)
		);
	}

	setExistingTags(tags: TagItem[]) {
		this.existingTags = tags;
	}

	setPendingTags(tags: TagItem[]) {
		this.pendingTags = tags;
	}

	setNewTags(tags: TagItem[]) {
		this.newTags = tags;
	}

	setSelectedTags(tags: TagItem[]) {
		this.selectedTags = tags;
	}

	setSearchQuery(query: string) {
		this.searchQuery = query;
	}

	setSortDirection(direction: TagsSortOrder) {
		this.sortDirection = direction;
	}

	setTagFound(found: boolean) {
		this.tagFound = found;
	}

	async search(name: string) {
		const response = await this.sendFetchRequest(name);
		this.setExistingTags(
			this.mapResponseTags(response.Tags).filter(
				existingTag =>
					!this.selectedTags.find(selectedTag =>
						TagUtils.equals(selectedTag.updatedName ?? selectedTag.originalName, existingTag.originalName)
					)
			)
		);
		this.setTagFound(!!this.existingTags.find(tag => TagUtils.equals(tag.originalName, name)));
		this.setSearchQuery(name);
	}

	addToPendingTags = () => {
		if (!this.newTags.length && !this.tagFound && this.searchQuery) {
			this.setPendingTags([...this.pendingTags, new TagItem(0, this.searchQuery)]);
			this.setSearchQuery('');
		}

		this.setPendingTags([...this.pendingTags, ...this.uniqueNewTags]);
		this.setNewTags([]);
	};

	addNewTag(tagName: string) {
		if (!this.newTags.find((tag: TagItem) => TagUtils.equals(tag.originalName, tagName))) {
			this.setNewTags([...this.newTags, new TagItem(0, tagName)]);
		}
	}

	deleteNewTag(tagName: string) {
		this.setNewTags(this.newTags.filter(newTag => !TagUtils.equals(newTag.originalName, tagName)));
	}

	selectTag = (selectedTag: TagItem) => {
		this.setSelectedTags([...this.selectedTags, selectedTag]);
		if (selectedTag.id === 0) {
			this.setPendingTags(
				this.pendingTags.filter(
					tag =>
						!TagUtils.equals(
							tag.updatedName ?? tag.originalName,
							selectedTag.updatedName ?? selectedTag.originalName
						)
				)
			);
		} else {
			this.setExistingTags(
				this.existingTags.filter(
					tag =>
						!TagUtils.equals(
							tag.updatedName ?? tag.originalName,
							selectedTag.updatedName ?? selectedTag.originalName
						)
				)
			);
		}
	};

	deleteSelectedTag = (deletedTag: TagItem) => {
		this.setSelectedTags(
			this.selectedTags.filter(
				tag =>
					!TagUtils.equals(
						tag.updatedName ?? tag.originalName,
						deletedTag.updatedName ?? deletedTag.originalName
					)
			)
		);
		if (deletedTag.id === 0) {
			this.setPendingTags([...this.pendingTags, deletedTag]);
		} else {
			this.setExistingTags([...this.existingTags, deletedTag]);
		}
	};

	sortTags = async (direction: TagsSortOrder) => {
		this.setSortDirection(direction);
		const response = await this.sendFetchRequest(this.searchQuery);
		this.setExistingTags(
			this.mapResponseTags(response.Tags).filter(
				existingTag =>
					!this.selectedTags.find(selectedTag =>
						TagUtils.equals(selectedTag.updatedName ?? selectedTag.originalName, existingTag.originalName)
					)
			)
		);
	};

	async fetchTags() {
		const response = await this.sendFetchRequest();
		this.setExistingTags(this.mapResponseTags(response.Tags));
	}

	async sendFetchRequest(tagName?: string) {
		this.cancellationToken = new AxiosCancellationToken();

		const request: ILibraryTagsRequest = {
			appliedTags: [],
			filter: tagName,
			libraryId: this.libraryId,
			page: 0,
			pageSize: 100,
			requestGuid: '00000000-0000-0000-0000-000000000000',
			tagsSort: this.sortDirection
		};
		const response = await this.lookupApiService.fetchLibraryTagsAsync(request, this.cancellationToken.token);

		return response;
	}

	mapResponseTags(items: PascalCase<IRFEListBoxItem>[]) {
		return items.map(
			tag => new TagItem(Number.parseInt(tag.Value), tag.Data, Number.parseInt(/\((\d+)\)/.exec(tag.Text)[1]))
		);
	}

	requestCancellation() {
		this.cancellationToken?.cancel();
	}

	async applyChanges(): Promise<IUpdateTagsResult> {
		return await this.sendApplyRequest([
			...this.existingTags,
			...this.pendingTags.filter(tag => !tag.deleted),
			...this.newTags
		]);
	}

	async sendApplyRequest(updatedTags: TagItem[]): Promise<IUpdateTagsResult> {
		this.cancellationToken = new AxiosCancellationToken();

		const request: IUpdateTagsRequest = {
			libraryId: this.libraryId,
			updatedTags: updatedTags
		};

		return await this.tagsApiService.updateTagsAsync(request, this.cancellationToken.token);
	}

	async addTagsWithInfoToDocuments(documentIds: number[]) {
		if (this.selectedTags.length) {
			await this.sendAddTagsWithInfoRequest(documentIds, this.selectedTags);
		}
	}

	async sendAddTagsWithInfoRequest(documentIds: number[], tags: TagItem[]) {
		this.cancellationToken = new AxiosCancellationToken();

		const request: IApplyTagsRequest = {
			documentIds: documentIds,
			libraryId: this.libraryId,
			tags: tags.map(tag => tag.updatedName ?? tag.originalName)
		};

		await this.tagsApiService.addTagsWithInfoAsync(request, this.cancellationToken.token);
	}

	addExistingTag(tag: TagItem): void {
		this.existingTags.push(tag);
	}

	resetExistingTags(): void {
		this.existingTags = [];
	}
}
